import { FormField, FieldType, StaticValue, ValidationModal2, O, option, FieldValue } from "../../../../../features/ui"
import { useEffect, useMemo, useState } from "react"
import { HoardOrder } from "../../../state/types"
import { Data2, DataTypeForm2, useDataTypesHooks } from "../../../../../features/data-types"
import { usePaymentTypeHooks, usePaymentTypeLoaders } from "../../../../administration"
import { FiscalDocumentStatus } from "../../../../fiscal-documents"
import { operationHelpers, PriceBreakdown, usePriceOutlineHooks } from "../../../../operations"
import { useHoardHooks } from "../../../hooks/hoardHooks"
import { useNavigate } from "react-router-dom"
import { useActions } from "./actions"

// En un acopio en cuenta corriente, la ampliación se puede cargar a la cuenta o cobrar en el momento
enum ChargeMode {
    CREDIT_ACCOUNT = 'creditAccount',
    COLLECT_NOW = 'collectNow'
}

type Props = {
    hoard: HoardOrder
}

export const IncreaseHoardForm = (props: Props) => {
    const { hoard } = props

    // Lo que paga el cliente (con recargo e IVA), igual que al crear un acopio financiero
    const [addedAmount, setAddedAmount] = useState<number>(0)
    const [customerCredit, setCustomerCredit] = useState<O<number>>()
    const [chargeMode, setChargeMode] = useState<ChargeMode>(ChargeMode.CREDIT_ACCOUNT)

    const { describeCode } = operationHelpers
    const { getPriceOutlineLiveFromTotal } = usePriceOutlineHooks()
    const { formatPrice } = useDataTypesHooks()
    const { isCreditAccount } = usePaymentTypeHooks()
    const { getAvailableCredit } = useHoardHooks()
    const paymentTypeLoaders = usePaymentTypeLoaders()
    const navigate = useNavigate()

    const { submitIncreaseHoard } = useActions()

    const isCreditAccountHoard = isCreditAccount(hoard.paymentTypeId)
    // Se carga a la cuenta corriente solo si el acopio es en cuenta corriente y no se elige cobrar ahora
    const isCredit = isCreditAccountHoard && chargeMode === ChargeMode.CREDIT_ACCOUNT
    const isInvoiced = [FiscalDocumentStatus.AUTHORIZED, FiscalDocumentStatus.TO_AUTHORIZE].includes(hoard.invoiceStatus)

    // El monto a ampliar es el total que paga el cliente: se discrimina con el recargo y el IVA del propio acopio
    const priceOutline = useMemo(() =>
        getPriceOutlineLiveFromTotal(addedAmount, hoard.paymentTypeYield, hoard.vatTaxYield)
    , [addedAmount, hoard.paymentTypeYield, hoard.vatTaxYield])

    // Mismo criterio que el formulario de acopio: el crédito a favor se suma al límite
    const getCustomerCredit = (dbRequest = false) => getAvailableCredit(hoard.customerId, dbRequest)

    useEffect(() => {
        isCreditAccountHoard && getCustomerCredit().then(setCustomerCredit)
    }, [])

    const onChangeChargeMode = (value?: FieldValue) => setChargeMode((value as O<ChargeMode>) || ChargeMode.CREDIT_ACCOUNT)

    const fields: FormField[] = [
        {
            name: 'code',
            type: FieldType.NUMBER,
            label: 'Acopio',
            render: () => <>{describeCode(hoard)}</>
        },
        {
            name: 'addedAmount',
            type: FieldType.PRICE,
            label: 'Monto a Ampliar',
            hint: 'Total a cobrar (con IVA)',
            onChange: (value) => setAddedAmount((value as number) || 0)
        },
        {
            name: 'priceBreakdown',
            type: FieldType.PRICE,
            label: 'Detalle',
            render: () => <PriceBreakdown data={priceOutline} />
        }
    ]

    if (isCreditAccountHoard) {
        const creditAccountName = paymentTypeLoaders().findPaymentType(hoard.paymentTypeId)?.name || 'Cuenta Corriente'
        fields.push({
            name: 'chargeMode',
            type: FieldType.SELECT,
            label: 'Forma de Pago',
            value: chargeMode,
            options: [
                option(ChargeMode.CREDIT_ACCOUNT, `Cargar a ${creditAccountName}`),
                option(ChargeMode.COLLECT_NOW, 'Cobrar ahora')
            ],
            onChange: onChangeChargeMode
        })
    }
    if (isCredit) {
        fields.push({
            name: 'customerCredit',
            type: FieldType.PRICE,
            label: 'Crédito Disponible',
            hint: 'Si no alcanza, se puede cobrar ahora',
            render: () => <StaticValue type={FieldType.PRICE} value={customerCredit} />
        })
    } else {
        fields.push({
            name: 'paymentInfo',
            type: FieldType.TEXT,
            label: '',
            render: () => <small className="text-muted">Al continuar vas a registrar el cobro del monto ampliado. La ampliación se guarda recién al confirmar el cobro.</small>
        })
    }

    if (isInvoiced) {
        fields.push({
            name: 'debitNoteInfo',
            type: FieldType.TEXT,
            label: '',
            render: () => <small className="text-muted">El acopio ya está facturado: se va a generar una nota de débito por este monto, pendiente de emitir.</small>
        })
    }

    const validate = async (): Promise<string[]> => {
        const validations: string[] = []
        if (!addedAmount || addedAmount <= 0) {
            validations.push('El monto a ampliar debe ser mayor a $0,00.')
        }
        if (validations.length === 0 && isCredit) {
            // Se consulta la base al confirmar para no depender de datos cacheados
            const updatedCustomerCredit = await getCustomerCredit(true)
            setCustomerCredit(updatedCustomerCredit)
            if (updatedCustomerCredit !== undefined && addedAmount > updatedCustomerCredit) {
                validations.push(`El monto a ampliar (${formatPrice(addedAmount)}) supera el crédito disponible (${formatPrice(updatedCustomerCredit)}) del cliente. Podés elegir "Cobrar ahora" como forma de pago.`)
            }
        }
        return validations
    }

    const onSubmit = async (_data: Data2) => {
        await submitIncreaseHoard(hoard.id, addedAmount, isCredit)
    }

    const onCancel = () => navigate(-1)

    return (
        <>
            <DataTypeForm2
                formId="increase-hoard-form"
                fields={fields}
                createLabel={isCredit ? 'Confirmar' : 'Continuar al Cobro'}
                onSubmit={onSubmit}
                onCancel={onCancel}
                validate={validate}
            />
            <ValidationModal2 title="Validaciones" />
        </>
    )
}
